import { useState } from 'react'
import { Bell, AlertTriangle, AlertCircle, Info, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { formatRelativeTime } from '@/lib/utils'
import type { AlertRow } from '@/api/types'

interface AlertMailboxProps {
  alerts: AlertRow[]
  onAcknowledge?: (id: string) => void
}

const severityIcons = {
  critical: AlertTriangle,
  warning: AlertCircle,
  info: Info,
}

const severityColors = {
  critical: 'text-destructive',
  warning: 'text-warning',
  info: 'text-muted-foreground',
}

export function AlertMailbox({ alerts, onAcknowledge }: AlertMailboxProps) {
  const [isOpen, setIsOpen] = useState(false)
  const unreadCount = alerts.filter((a) => !a.acknowledged).length

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-2 w-80 rounded-lg border bg-card shadow-lg">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <span className="font-semibold">Notifications</span>
              {unreadCount > 0 && (
                <Badge variant="secondary">{unreadCount} unread</Badge>
              )}
            </div>
            <ScrollArea className="h-[300px]">
              {alerts.length === 0 ? (
                <div className="flex h-full items-center justify-center p-4 text-sm text-muted-foreground">
                  No notifications
                </div>
              ) : (
                <div className="divide-y">
                  {alerts.map((alert) => {
                    const Icon = severityIcons[alert.severity]
                    return (
                      <div
                        key={alert.id}
                        className={`flex gap-3 p-3 ${!alert.acknowledged ? 'bg-muted/50' : ''}`}
                      >
                        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${severityColors[alert.severity]}`} />
                        <div className="flex-1 space-y-1">
                          <p className="text-sm leading-tight">{alert.message}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">
                              {formatRelativeTime(alert.created_at)}
                            </span>
                            {!alert.acknowledged && onAcknowledge && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs"
                                onClick={() => onAcknowledge(alert.id)}
                              >
                                <Check className="mr-1 h-3 w-3" />
                                Dismiss
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
        </>
      )}
    </div>
  )
}
